import { supabase, unwrapFunctionError } from '../supabase';
import type { Category, ContentCard, ContentDetail, FeedItem, Story, Subcategory } from '../types';
import { storyTime } from '../format';
import {
  CARD_COLUMNS,
  DETAIL_COLUMNS,
  FEED_COLUMNS,
  assembleStory,
  pickLangCards,
  type SourceLite,
  type StoryDetail,
  type TagRow,
} from '../story-detail';

// Hikâye detayının birleştirme mantığı ../story-detail'de (kenar fonksiyonuyla ortak).
export { FEED_COLUMNS, pickLangCards };
export type { SourceLite, StoryDetail, StoryMember } from '../story-detail';

/** Kümelenmiş orijinalleri gizle; hikâyeyi sentez satırı temsil eder. */
export const VISIBLE = 'cluster_id.is.null,is_synthesis.eq.true';
export const RECO_MAX_AGE_HOURS = 48;

export function recoSince(hours = RECO_MAX_AGE_HOURS): string {
  return new Date(Date.now() - hours * 3600_000).toISOString();
}

export class QuotaError extends Error {
  code: 'premium_required' | 'quota_exceeded';
  constructor(code: 'premium_required' | 'quota_exceeded') {
    super(code);
    this.code = code;
  }
}

export function throwIfQuota(data: unknown): void {
  const r = data as { code?: string } | null;
  if (r?.code === 'premium_required' || r?.code === 'quota_exceeded') throw new QuotaError(r.code);
}

// ---------- Taksonomi ve kaynak haritaları (oturum boyunca önbellekli) ----------

let categoriesP: Promise<Category[]> | null = null;
export function getCategories(): Promise<Category[]> {
  categoriesP ??= (async () => {
    const { data, error } = await supabase.from('categories').select('id,slug,name,icon,sort').order('sort');
    if (error) {
      categoriesP = null;
      throw error;
    }
    return (data ?? []) as Category[];
  })();
  return categoriesP;
}

let subcategoriesP: Promise<Subcategory[]> | null = null;
export function getSubcategories(): Promise<Subcategory[]> {
  subcategoriesP ??= (async () => {
    const { data, error } = await supabase.from('subcategories').select('id,category_id,slug,name').order('name');
    if (error) {
      subcategoriesP = null;
      throw error;
    }
    return (data ?? []) as Subcategory[];
  })();
  return subcategoriesP;
}

let sourcesP: Promise<Map<string, SourceLite>> | null = null;
export function getSourceMap(): Promise<Map<string, SourceLite>> {
  sourcesP ??= (async () => {
    const { data, error } = await supabase.from('sources').select('id,title,site_url,logo_url,kind');
    if (error) {
      sourcesP = null;
      throw error;
    }
    return new Map((data ?? []).map((s) => [s.id, s as SourceLite]));
  })();
  return sourcesP;
}
export function invalidateSourceMap() {
  sourcesP = null;
}

// ---------- Kart dili: pickLangCards ../story-detail'de ----------

/**
 * Akış öğelerine kapak kartının başlığını ve özünü ekler. content.title karışık
 * dilde (eski satırlar TR, yeniler EN); doğru başlık her dilde kapak kartında.
 */
export async function withDeks(items: FeedItem[], lang: string): Promise<Story[]> {
  if (items.length === 0) return [];
  const ids = items.map((i) => i.id);
  const langs = [...new Set([lang, 'en'])];
  const rows: { content_id: string; heading: string | null; body: string | null; lang: string }[] = [];
  // PostgREST URL uzunluğu için parça parça.
  for (let i = 0; i < ids.length; i += 80) {
    const { data } = await supabase
      .from('content_cards')
      .select('content_id,heading,body,lang')
      .in('content_id', ids.slice(i, i + 80))
      .eq('kind', 'cover')
      .eq('style', 'expert')
      .in('lang', langs);
    rows.push(...(data ?? []));
  }
  const best = new Map<string, { heading: string | null; body: string | null; rank: number }>();
  for (const r of rows) {
    const rank = langs.indexOf(r.lang);
    const prev = best.get(r.content_id);
    if (!prev || rank < prev.rank) best.set(r.content_id, { heading: r.heading, body: r.body, rank });
  }
  return items.map((it) => {
    const b = best.get(it.id);
    return { ...it, heading: b?.heading?.trim() || it.title, dek: b?.body?.trim() || null };
  });
}

// ---------- Akışlar ----------

export type DiscoverData = { stories: Story[]; stale: boolean };

/**
 * Keşfet: son 48 saatin görünür hikâyeleri. Hat durduğunda (ör. sağlayıcı
 * kilidi) sayfa boş kalmasın diye en son yayımlananlara düşer ve bunu belirtir.
 */
export async function fetchDiscover(lang: string, limit = 120): Promise<DiscoverData> {
  const { data, error } = await supabase
    .from('content')
    .select(FEED_COLUMNS)
    .or(VISIBLE)
    .gte('published_at', recoSince())
    .lte('published_at', new Date(Date.now() + 3600_000).toISOString())
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  let items = (data ?? []) as FeedItem[];
  let stale = false;
  if (items.length < 12) {
    const { data: older, error: oe } = await supabase
      .from('content')
      .select(FEED_COLUMNS)
      .or(VISIBLE)
      .lte('published_at', new Date(Date.now() + 3600_000).toISOString())
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(60);
    if (oe) throw oe;
    const seen = new Set(items.map((i) => i.id));
    items = [...items, ...((older ?? []) as FeedItem[]).filter((i) => !seen.has(i.id))];
    stale = true;
  }
  return { stories: await withDeks(items, lang), stale };
}

/** Gündemde: discover_popular görünümü (48s etkileşim, yoksa rastgele dolgu). */
export async function fetchTrending(lang: string, n = 8): Promise<Story[]> {
  const { data } = await supabase
    .from('discover_popular')
    .select('content_id,score')
    .order('score', { ascending: false })
    .limit(n * 3);
  const ids = (data ?? []).map((r) => r.content_id as string);
  const items = await contentByIds(ids);
  const stories = await withDeks(items, lang);
  return stories.filter((s) => s.cover_image_url || s.dek).slice(0, n);
}

export async function contentByIds(ids: string[]): Promise<FeedItem[]> {
  if (ids.length === 0) return [];
  const out: FeedItem[] = [];
  for (let i = 0; i < ids.length; i += 100) {
    const { data, error } = await supabase.from('content').select(FEED_COLUMNS).in('id', ids.slice(i, i + 100));
    if (error) throw error;
    out.push(...((data ?? []) as FeedItem[]));
  }
  const order = new Map(ids.map((id, i) => [id, i]));
  return out.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

export async function storiesByIds(ids: string[], lang: string): Promise<Story[]> {
  return withDeks(await contentByIds(ids), lang);
}

/** Kategori akışı — content_tags üzerinden (birincil + ikincil etiket). */
export async function fetchByCategory(categoryId: string, subcategoryId: string | null, lang: string, limit = 60): Promise<Story[]> {
  let tq = supabase
    .from('content_tags')
    .select('content_id')
    .eq('category_id', categoryId)
    .order('created_at', { ascending: false })
    .limit(limit * 3);
  if (subcategoryId) tq = tq.eq('subcategory_id', subcategoryId);
  const { data: tags, error: te } = await tq;
  if (te) throw te;
  const ids = [...new Set((tags ?? []).map((t) => t.content_id as string))];
  if (ids.length === 0) return [];
  const out: FeedItem[] = [];
  for (let i = 0; i < ids.length; i += 100) {
    const { data, error } = await supabase
      .from('content')
      .select(FEED_COLUMNS)
      .in('id', ids.slice(i, i + 100))
      .or(VISIBLE);
    if (error) throw error;
    out.push(...((data ?? []) as FeedItem[]));
  }
  const sorted = out.sort((a, b) => storyTime(b) - storyTime(a)).slice(0, limit);
  return withDeks(sorted, lang);
}

export async function fetchBySource(sourceId: string, lang: string, limit = 40): Promise<Story[]> {
  const { data, error } = await supabase
    .from('content')
    .select(FEED_COLUMNS)
    .eq('source_id', sourceId)
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return withDeks((data ?? []) as FeedItem[], lang);
}

/** Başlık + metin araması. PostgREST filtre karakterleri temizlenir. */
export async function searchStories(query: string, lang: string, limit = 20): Promise<Story[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const pattern = `%${q.replace(/[%_,().\\*:]/g, ' ').trim()}%`;
  if (pattern === '%%') return [];
  const { data, error } = await supabase
    .from('content')
    .select(FEED_COLUMNS)
    .ilike('title', pattern)
    .or(VISIBLE)
    .order('published_at', { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) throw error;
  return withDeks((data ?? []) as FeedItem[], lang);
}

// ---------- Hikâye detayı ----------

export async function fetchStory(id: string, lang: string): Promise<StoryDetail | null> {
  const [contentRes, cardsRes, cats, sources, tagsRes, subs] = await Promise.all([
    supabase.from('content').select(DETAIL_COLUMNS).eq('id', id).maybeSingle(),
    supabase.from('content_cards').select(CARD_COLUMNS).eq('content_id', id).order('idx'),
    getCategories(),
    getSourceMap(),
    supabase.from('content_tags').select('category_id,subcategory_id,kind').eq('content_id', id),
    getSubcategories(),
  ]);
  if (contentRes.error) throw contentRes.error;
  if (!contentRes.data) return null;
  const content = contentRes.data as ContentDetail;

  let members: FeedItem[] = [];
  if (content.is_synthesis && content.cluster_id) {
    const { data } = await supabase
      .from('content')
      .select(FEED_COLUMNS)
      .eq('cluster_id', content.cluster_id)
      .eq('is_synthesis', false)
      .order('published_at', { ascending: false, nullsFirst: false })
      .limit(40);
    members = (data ?? []) as FeedItem[];
  }

  return assembleStory(
    {
      content,
      cards: (cardsRes.data ?? []) as ContentCard[],
      categories: cats,
      subcategories: subs,
      sources,
      tagRows: (tagsRes.data ?? []) as TagRow[],
      members,
    },
    lang
  );
}

/** İstenen dilde kart yoksa sunucuda çevir (premium kotası; önbellekliyse ücretsiz). */
export async function translateStory(contentId: string, lang: string): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke('translate', { body: { contentId, lang } });
  if (error) throw await unwrapFunctionError(error);
  throwIfQuota(data);
  return !!(data as { ok?: boolean })?.ok;
}

export async function fetchSimilar(contentId: string, lang: string, n = 6): Promise<Story[]> {
  const { data, error } = await supabase.rpc('match_similar_content', { p_content_id: contentId, p_count: n + 4 });
  if (error) return [];
  const ids = ((data ?? []) as { id: string }[]).map((r) => r.id).filter((x) => x !== contentId);
  if (ids.length === 0) return [];
  const items = (await contentByIds(ids)).filter((i) => !i.cluster_id || i.is_synthesis);
  return (await withDeks(items, lang)).slice(0, n);
}

/** Kaynak listesi kartının gövdesi: "Ad — Başlık\nURL" blokları. */
export function parseSourceList(body: string | null): { name: string; title: string; url: string | null }[] {
  return (body ?? '')
    .split(/\n{2,}/)
    .map((block) => {
      const [line, url] = block.split('\n');
      const [name, ...rest] = (line ?? '').split(' — ');
      return { name: (name ?? '').trim(), title: rest.join(' — ').trim(), url: url?.trim() || null };
    })
    .filter((x) => x.name);
}

export async function getServerTTS(contentId: string, lang: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('tts', { body: { contentId, lang } });
  if (error) throw await unwrapFunctionError(error);
  throwIfQuota(data);
  const url = (data as { audio_url?: string })?.audio_url;
  if (!url) throw new Error('tts');
  return url;
}

export async function getCardsForSpeech(contentId: string, lang: string): Promise<string[]> {
  const { data } = await supabase
    .from('content_cards')
    .select('id,content_id,idx,kind,heading,body,lang,style')
    .eq('content_id', contentId)
    .order('idx');
  const cards = pickLangCards((data ?? []) as ContentCard[], lang).filter((c) => c.kind === 'cover' || c.kind === 'body');
  return cards.map((c) => [c.heading, c.body].filter(Boolean).join('. '));
}
